import find from 'lodash/find';
import React, { FC, ReactChild, useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import set from 'lodash/set';
import get from 'lodash/get';

import FieldContainer from './FieldContainer';
import FormContext from 'src/presentation/contexts/FormContext';
import Toggle from 'src/presentation/components/forms/Toggle';

type InputProps = {
  attr: string;
  'data-aid'?: string;
  description?: ReactChild;
  disabled?: boolean;
  /*
  @param flipValue: display this value as the boolean opposite. Useful when backend is "disabled" but display is "enabled"
  */
  flipValue?: boolean;
  label: string;
  onChange?: Function;
  transformValue?: any;
  /*
  @param useLocalControlState: if true, uses temporary "working state" instead of form-level "updateState" to avoid
  re-rendering the entire form on change. ONLY USE FOR SWITCHES THAT DO NOT HAVE DEPENDENCIES (i.e. do not affect
  other controls on the form)
   */
  useLocalControlState?: boolean;
};

const Switch: FC<InputProps> = ({
  attr,
  'data-aid': dataAid,
  description,
  disabled,
  flipValue,
  label,
  onChange,
  useLocalControlState,
}) => {
  const { errors, workingState, updateState } = useContext(FormContext);
  const [, forceRedraw] = useState({});

  const error = find(errors, error => error.attr === attr);
  const defaultOnToggleChange = useCallback(
    value => {
      const updatedValue = flipValue ? !value : !!value;
      if (useLocalControlState) {
        set(workingState, attr, updatedValue);
        forceRedraw({});
      } else {
        updateState(set({}, attr, updatedValue));
      }
    },
    [attr, flipValue, updateState, workingState, useLocalControlState]
  );
  const onToggleChange = onChange || defaultOnToggleChange;

  return (
    <ToggleContainer
      error={error ? error.detail : undefined}
      label={label}
      description={description}
      control={
        <Toggle
          data-aid={dataAid}
          disabled={disabled}
          value={flipValue ? !get(workingState, attr) : !!get(workingState, attr)}
          onChange={onToggleChange}
        />
      }
    />
  );
};

const ToggleContainer = styled(FieldContainer)`
  flex-grow: 0;
`;

export default Switch;
